/**
 * features/documents/DocumentItemsEditor.tsx — ตารางแก้ไขรายการสินค้าในเอกสาร
 *
 * ใช้ร่วมกันทั้งใบเสนอราคาและใบกำกับภาษี
 * ยอดเงินที่แสดงเป็นแค่ preview ให้ผู้ใช้เห็นทันที ยอดจริงคำนวณที่ server ตอนบันทึก
 */

import { GripVertical, Plus, Trash2 } from 'lucide-react';
import { computeLineAmount, formatMoney } from '@ft/core';
import { useDebounced } from '~/lib/use-debounced';
import { Combobox } from '~/ui/Combobox';
import { Button, Input } from '~/ui/primitives';
import { useProductSearch } from '~/features/products/use-product-search';
import type { DocumentItemDraft } from './types';

const EMPTY_ITEM: DocumentItemDraft = {
  productName: '',
  description: '',
  quantity: 1,
  unit: 'ชิ้น',
  unitPrice: 0,
  discount: 0,
};

interface DocumentItemsEditorProps {
  items: DocumentItemDraft[];
  onChange: (items: DocumentItemDraft[]) => void;
  errors?: string[];
}

export function DocumentItemsEditor({ items, onChange, errors }: DocumentItemsEditorProps) {
  function updateItem(index: number, patch: Partial<DocumentItemDraft>) {
    onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  }

  function addItem() {
    onChange([...items, { ...EMPTY_ITEM }]);
  }

  function removeItem(index: number) {
    onChange(items.filter((_, i) => i !== index));
  }

  return (
    <div className="space-y-2">
      <div className="hidden grid-cols-[24px_1fr_90px_80px_110px_110px_32px] gap-2 px-1 text-xs font-medium text-fg-muted md:grid">
        <span />
        <span>รายการ</span>
        <span className="text-right">จำนวน</span>
        <span>หน่วย</span>
        <span className="text-right">ราคา/หน่วย</span>
        <span className="text-right">จำนวนเงิน</span>
        <span />
      </div>

      {items.map((item, index) => (
        <ItemRow
          key={index}
          item={item}
          error={errors?.[index]}
          onChange={(patch) => updateItem(index, patch)}
          onRemove={items.length > 1 ? () => removeItem(index) : undefined}
        />
      ))}

      <Button variant="ghost" size="sm" onClick={addItem} className="mt-1">
        <Plus size={14} />
        เพิ่มรายการ
      </Button>
    </div>
  );
}

function ItemRow({
  item,
  error,
  onChange,
  onRemove,
}: {
  item: DocumentItemDraft;
  error?: string;
  onChange: (patch: Partial<DocumentItemDraft>) => void;
  onRemove?: () => void;
}) {
  const debouncedName = useDebounced(item.productName);
  const { data, isFetching } = useProductSearch(debouncedName);

  const amount = computeLineAmount({
    quantity: item.quantity,
    unitPrice: item.unitPrice,
    discount: item.discount,
  });

  return (
    <div className="rounded-md border border-border-base p-2.5 md:border-0 md:p-0">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-[24px_1fr_90px_80px_110px_110px_32px] md:items-start">
        <div className="hidden items-center justify-center pt-2 text-fg-subtle md:flex">
          <GripVertical size={14} aria-hidden />
        </div>

        <div>
          <Combobox
            value={item.productName}
            onChange={(value) => {
              onChange({ productName: value });
            }}
            onSelect={(product) => {
              onChange({
                productName: product.name,
                unitPrice: product.price,
                unit: product.unit || item.unit,
              });
            }}
            options={data?.data ?? []}
            isLoading={isFetching}
            getLabel={(product) => product.name}
            getSublabel={(product) => formatMoney(product.price)}
            placeholder="ชื่อสินค้า/บริการ"
            invalid={Boolean(error)}
          />
          <Input
            value={item.description}
            onChange={(event) => onChange({ description: event.target.value })}
            placeholder="รายละเอียดเพิ่มเติม (ถ้ามี)"
            className="mt-1.5"
          />
          {error ? <p className="mt-1 text-xs text-danger">{error}</p> : null}
        </div>

        <Input
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={item.quantity}
          onChange={(event) => onChange({ quantity: Number.parseFloat(event.target.value) || 0 })}
          className="text-right"
          aria-label="จำนวน"
        />

        <Input
          value={item.unit}
          onChange={(event) => onChange({ unit: event.target.value })}
          aria-label="หน่วย"
        />

        <Input
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={item.unitPrice}
          onChange={(event) => onChange({ unitPrice: Number.parseFloat(event.target.value) || 0 })}
          className="text-right"
          aria-label="ราคาต่อหน่วย"
        />

        <div className="tabular flex h-9 items-center justify-end px-2 text-sm font-medium text-fg">
          {formatMoney(amount)}
        </div>

        {onRemove ? (
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            onClick={onRemove}
            aria-label="ลบรายการนี้"
            className="justify-self-end md:justify-self-auto"
          >
            <Trash2 size={14} />
          </Button>
        ) : (
          <span />
        )}
      </div>
    </div>
  );
}
